import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { ContentCopy, Delete } from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";

/**
 * NodeWrapper V2 — Enterprise quality wrapper for all flow nodes.
 *
 * Props:
 *   id, icon, title, subtitle, color, bgColor, borderColor, children,
 *   handles[], noTarget, noSource, isConnectable, minWidth, selected,
 *   targetHandleId — custom target handle id (e.g. "httpRequest-in")
 */
const NodeWrapper = memo(({
  id,
  icon,
  title,
  subtitle,
  color = "#9a00ed",
  bgColor = "#fff",
  borderColor,
  children,
  handles = [],
  noTarget = false,
  noSource = false,
  isConnectable = true,
  minWidth = 200,
  selected = false,
  targetHandleId,
  data,
}) => {
  const storageItems = useNodeStorage();

  // Aparencia overrides from Inspector
  const effectiveBgColor = data?.bgColor || bgColor;
  const effectiveTitle = data?.labelOverride || title;
  const border = borderColor || `${color}50`;

  return (
    <div
      style={{
        backgroundColor: effectiveBgColor,
        padding: "10px 12px",
        borderRadius: 10,
        border: `2px solid ${selected ? color : border}`,
        boxShadow: selected
          ? `0 0 0 2px ${color}25, 0 4px 16px rgba(0,0,0,0.10)`
          : "0 2px 8px rgba(0,0,0,0.06)",
        transition: "background-color 0.2s, box-shadow 0.2s, border-color 0.2s",
        minWidth,
        maxWidth: 320,
        position: "relative",
        transition: "box-shadow 0.2s, border-color 0.2s",
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
      }}
    >
      {/* Target handle */}
      {!noTarget && (
        <Handle
          type="target"
          position={Position.Left}
          id={targetHandleId || undefined}
          style={{
            background: color,
            width: 16,
            height: 16,
            left: -9,
            border: `2.5px solid ${effectiveBgColor}`,
            borderRadius: "50%",
            cursor: "crosshair",
          }}
          isConnectable={isConnectable}
        />
      )}

      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: children || subtitle ? 6 : 0,
      }}>
        {icon && (
          <span style={{
            color,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}>
            {React.cloneElement(icon, {
              style: { fontSize: 18, color, ...icon.props?.style },
            })}
          </span>
        )}
        <span style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#1a1a1a",
          flex: 1,
          letterSpacing: "-0.01em",
        }}>
          {effectiveTitle}
        </span>

        {/* Actions */}
        <span style={{
          display: "flex",
          gap: 2,
          opacity: selected ? 0.7 : 0.3,
          transition: "opacity 0.15s",
        }}>
          <ContentCopy
            onClick={(e) => {
              e.stopPropagation();
              storageItems.setNodesStorage(id);
              storageItems.setAct("duplicate");
            }}
            style={{ fontSize: 13, color: "#666", cursor: "pointer" }}
          />
          <Delete
            onClick={(e) => {
              e.stopPropagation();
              storageItems.setNodesStorage(id);
              storageItems.setAct("delete");
            }}
            style={{ fontSize: 13, color: "#666", cursor: "pointer" }}
          />
        </span>
      </div>

      {/* Subtitle */}
      {subtitle && (
        <div style={{
          fontSize: 11,
          color: "#666",
          marginBottom: children ? 6 : 0,
          lineHeight: 1.4,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}>
          {subtitle}
        </div>
      )}

      {/* Content */}
      {children}

      {/* Default source handle */}
      {!noSource && handles.length === 0 && (
        <Handle
          type="source"
          position={Position.Right}
          id="a"
          style={{
            background: color,
            width: 16,
            height: 16,
            right: -9,
            border: `2.5px solid ${effectiveBgColor}`,
            borderRadius: "50%",
            cursor: "crosshair",
          }}
          isConnectable={isConnectable}
        />
      )}

      {/* Custom handles */}
      {handles.map((h) => (
        <Handle
          key={h.id}
          type={h.type || "source"}
          position={h.position || Position.Right}
          id={h.id}
          style={{
            background: h.color || color,
            width: 14,
            height: 14,
            border: `2px solid ${effectiveBgColor}`,
            borderRadius: "50%",
            cursor: "crosshair",
            ...(h.style || {}),
          }}
          isConnectable={isConnectable}
        />
      ))}
    </div>
  );
});

NodeWrapper.displayName = "NodeWrapper";
export default NodeWrapper;
